'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Impact } from '@/utils/calendar/calendarFormat';
import ImpactDots from '@/components/(blog)/post/calendar/ImpactDots';

export interface EventRow {
    id: string;
    /** Tehran day start (seconds) and its label, worked out on the server. */
    day: number;
    dayLabel: string;
    ts: number;
    time: string;
    tentative: boolean;
    countryFa: string;
    currency: string;
    title: string;
    /** English name, only when it differs from `title`. */
    titleEn: string | null;
    impact: Impact;
    released: boolean;
    previous: string;
    forecast: string;
    actual: string;
    /** In the default view (high importance) or only in «مهم و متوسط». */
    important: boolean;
    guide: { id: string; title: string; summary: string } | null;
}

const Value: React.FC<{ label: string; value: string; strong?: boolean }> = ({
    label,
    value,
    strong,
}) => (
    <span className='flex flex-col items-start gap-0.5 sm:items-center'>
        <span className='text-[11px] text-gray-500 dark:text-gray-400 sm:hidden'>{label}</span>
        <span
            dir='ltr'
            className={`whitespace-nowrap text-xs ${strong ? 'font-bold text-zinc-900 dark:text-white' : 'text-zinc-700 dark:text-gray-300'}`}
        >
            {value}
        </span>
    </span>
);

/**
 * This week's events, grouped by Tehran day. The important rows are in the HTML (search engines read them);
 * «مهم و متوسط» adds the medium-importance rows, which come with the page (no request).
 */
const WeekEventsTable: React.FC<{ rows: EventRow[] }> = ({ rows }) => {
    const [all, setAll] = useState(false);
    // Marked after mount only, so the server HTML and the first client render match
    const [now, setNow] = useState<number | null>(null);
    useEffect(() => {
        setNow(Math.floor(Date.now() / 1000));
        const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 60_000);
        return () => clearInterval(timer);
    }, []);

    const shown = useMemo(() => (all ? rows : rows.filter((r) => r.important)), [all, rows]);
    const days = useMemo(() => {
        const map = new Map<number, { label: string; rows: EventRow[] }>();
        shown.forEach((r) => {
            if (!map.has(r.day)) map.set(r.day, { label: r.dayLabel, rows: [] });
            map.get(r.day)!.rows.push(r);
        });
        return [...map.entries()];
    }, [shown]);
    const nextId = now ? shown.find((r) => r.ts > now && !r.released)?.id : null;
    const importantCount = rows.filter((r) => r.important).length;

    const tab = (active: boolean) =>
        `rounded-lg px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
            active
                ? 'bg-white dark:bg-white/15 text-zinc-900 dark:text-white shadow-xs'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
        }`;

    return (
        <div>
            <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
                <div
                    role='tablist'
                    className='flex gap-1 rounded-xl bg-gray-100 dark:bg-white/5 p-1'
                >
                    <button
                        type='button'
                        role='tab'
                        aria-selected={!all}
                        className={tab(!all)}
                        onClick={() => setAll(false)}
                    >
                        فقط مهم ({importantCount.toLocaleString('fa-IR')})
                    </button>
                    <button
                        type='button'
                        role='tab'
                        aria-selected={all}
                        className={tab(all)}
                        onClick={() => setAll(true)}
                    >
                        مهم و متوسط ({rows.length.toLocaleString('fa-IR')})
                    </button>
                </div>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                    همهٔ ساعت‌ها به وقت تهران (UTC+۳:۳۰)
                </span>
            </div>

            {days.length === 0 && (
                <p className='rounded-xl bg-gray-50 dark:bg-white/5 p-4 text-sm text-gray-600 dark:text-gray-300'>
                    این هفته رویداد مهمی ثبت نشده است.
                </p>
            )}

            <div className='flex flex-col gap-5'>
                {days.map(([day, group]) => {
                    const isToday = now !== null && now >= day && now < day + 86400;
                    return (
                        <section key={day} aria-label={group.label}>
                            <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-zinc-900 dark:text-white'>
                                {group.label}
                                {isToday && (
                                    <span className='rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium text-white dark:bg-white dark:text-primary'>
                                        امروز
                                    </span>
                                )}
                            </h3>
                            <div className='overflow-hidden rounded-xl border border-gray-100 dark:border-white/10'>
                                <div className='hidden grid-cols-[4.5rem_6.5rem_1fr_4.5rem_4.5rem_4.5rem] gap-2 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 dark:bg-white/5 dark:text-gray-400 sm:grid'>
                                    <span>ساعت</span>
                                    <span>کشور</span>
                                    <span>رویداد</span>
                                    <span className='text-center'>قبلی</span>
                                    <span className='text-center'>پیش‌بینی</span>
                                    <span className='text-center'>واقعی</span>
                                </div>
                                <ul className='divide-y divide-gray-100 dark:divide-white/10'>
                                    {group.rows.map((r) => {
                                        const past = now !== null && r.ts < now;
                                        return (
                                            <li
                                                key={r.id}
                                                className={`grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 px-3 py-2.5 sm:grid-cols-[4.5rem_6.5rem_1fr_4.5rem_4.5rem_4.5rem] sm:items-start sm:gap-2 ${
                                                    r.id === nextId
                                                        ? 'bg-amber-50 dark:bg-amber-500/10'
                                                        : ''
                                                }`}
                                            >
                                                <span className='flex flex-col text-xs font-bold text-zinc-900 dark:text-white'>
                                                    {r.time}
                                                    {r.tentative && (
                                                        <span className='text-[11px] font-normal text-gray-500'>
                                                            تقریبی
                                                        </span>
                                                    )}
                                                    {r.id === nextId && (
                                                        <span className='text-[11px] font-medium text-amber-700 dark:text-amber-300'>
                                                            بعدی
                                                        </span>
                                                    )}
                                                </span>
                                                <span className='flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 sm:flex-col sm:items-start sm:gap-1'>
                                                    <span>
                                                        {r.countryFa}
                                                        {r.currency && (
                                                            <span className='text-gray-400'>
                                                                {' '}
                                                                · {r.currency}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <ImpactDots impact={r.impact} />
                                                </span>
                                                <div className='col-span-2 min-w-0 sm:col-span-1'>
                                                    <p className='text-sm font-medium leading-6 text-zinc-900 dark:text-white'>
                                                        {r.title}
                                                    </p>
                                                    {r.titleEn && (
                                                        <p
                                                            dir='ltr'
                                                            className='text-right text-xs text-gray-500 dark:text-gray-400'
                                                        >
                                                            {r.titleEn}
                                                        </p>
                                                    )}
                                                    {r.guide && (
                                                        <details className='group mt-1'>
                                                            <summary className='inline-flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-primary dark:text-blue-300'>
                                                                این شاخص چیست؟
                                                                <ChevronDown
                                                                    size={12}
                                                                    className='transition-transform group-open:rotate-180'
                                                                />
                                                            </summary>
                                                            <p className='mt-1 text-xs leading-6 text-gray-600 dark:text-gray-300'>
                                                                {r.guide.summary}{' '}
                                                                <a
                                                                    href={`#guide-${r.guide.id}`}
                                                                    className='font-medium text-primary underline dark:text-blue-300'
                                                                >
                                                                    بیشتر
                                                                </a>
                                                            </p>
                                                        </details>
                                                    )}
                                                </div>
                                                <div className='col-span-2 grid grid-cols-3 gap-2 sm:contents'>
                                                    <Value label='قبلی' value={r.previous} />
                                                    <Value label='پیش‌بینی' value={r.forecast} />
                                                    <Value
                                                        label='واقعی'
                                                        value={
                                                            r.released || r.actual !== '—'
                                                                ? r.actual
                                                                : past
                                                                  ? 'در انتظار'
                                                                  : '—'
                                                        }
                                                        strong
                                                    />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </section>
                    );
                })}
            </div>
            <p className='mt-3 text-xs text-gray-500 dark:text-gray-400'>
                «پیش‌بینی» برآورد منبع داده است، نه نظرسنجی رسمی. عدد واقعی چند دقیقه بعد از انتشار
                اینجا می‌نشیند.
            </p>
        </div>
    );
};

export default WeekEventsTable;
